import React, { useCallback, useContext, useState, useEffect } from 'react';
import { IEditorProps } from '../Editor';
import { AssignmentField } from '../../assignments/AssignmentField';
import { getAssignees, convertServerValue, getDeletedAssignees } from './utils/AssignmentUtils';
import { EditorLabel } from '../EditorLabel';
import { AssigneeDto } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Assignments/AssigneeDto';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { search } from 'components/assignments/utils/AssignmentUtils';
import { EditorValidationWrapper } from '../EditorValidationWrapper';

interface IEditorAssigneesProps extends IEditorProps {
  placeholder?: string; // pass this in for i18n
}

/**
 * Editor for assignees. Displays current assignees, and shows assignees available.
 *
 * Copyright (c) 2019 Decisions ALl Rights Reserved
 */
export function EditorAssignees({ child, path, id }: IEditorAssigneesProps) {
  const [assignees, setAssignees] = useState<AssigneeDto[]>([]);
  const [deletedAssignees, setDeletedAssignees] = useState<Map<number, AssigneeDto>>(new Map());
  const { onChange, editingContextId } = useContext(PropertyEditorContext);

  const assigneeString = getAssignees(child)
    .map(assignee => assignee.Id)
    .join(',');

  useEffect(() => {
    setAssignees(getAssignees(child, deletedAssignees));
    // NOTE: only update for assigneeString string, because child.EditingContextId was almost
    // always null, and not reliable when clicking between steps of same type.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assigneeString]);

  useEffect(() => {
    // clear deleted map if editing context (step) ID changes.
    setDeletedAssignees(new Map());
  }, [editingContextId]);

  const onChangeAssignments = useCallback(
    (assignees: AssigneeDto[]) => {
      // track deleted ones so we can restore them.
      setDeletedAssignees(getDeletedAssignees(assignees));
      const value = convertServerValue(assignees);
      onChange(value, path);
    },
    [onChange, path]
  );

  return (
    <EditorLabel editor={child} path={path} className="dpc-assignees-editor" htmlFor={id} doNotWrap>
      <EditorValidationWrapper editor={child}>
        <AssignmentField assignees={assignees} search={search} onChange={onChangeAssignments} />
      </EditorValidationWrapper>
    </EditorLabel>
  );
}
